import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";

const deadline = () => new Date(`${new Date().getFullYear()}-12-24T23:59:59+11:00`);
const seasonStart = () => new Date(`${new Date().getFullYear()}-10-01T00:00:00+10:00`);

const remaining = () => {
  const ms = deadline() - Date.now();
  if (ms <= 0 || Date.now() < seasonStart()) return null;
  return { d: Math.floor(ms / 864e5), h: Math.floor((ms / 36e5) % 24), m: Math.floor((ms / 6e4) % 60), s: Math.floor((ms / 1e3) % 60) };
};

const VARIANTS = {
  all: {
    to: "/gift-vouchers", title: "Christmas Gift Cards", sub: "For any service: recording, mixing, DJ room, photography & more",
    wrap: "bg-[#7f1d1d] border-y border-amber-300/30", accent: "text-amber-300", box: "bg-black/30 text-white", btn: "bg-amber-300 text-black hover:bg-white",
  },
  junior: {
    to: "#jr-gift", title: "Christmas Junior DJ Vouchers", sub: "Gift a Casual Private Lesson or the 4-Week DJ Starter",
    wrap: "bg-black border-y-4 border-lime-300", accent: "text-lime-300", box: "bg-lime-300 text-black", btn: "bg-pink-400 text-black hover:bg-lime-300",
  },
};

const Unit = ({ value, label, box, testId }) => (
  <div className={`rounded-lg px-2.5 py-1.5 text-center min-w-[52px] ${box}`} data-testid={testId}>
    <p className="font-mono text-lg font-bold leading-none tabular-nums">{String(value).padStart(2, "0")}</p>
    <p className="text-[9px] uppercase tracking-widest opacity-70 mt-1">{label}</p>
  </div>
);

export const ChristmasVoucherBanner = ({ scope = "all", className = "" }) => {
  const [left, setLeft] = useState(remaining);
  useEffect(() => {
    const t = setInterval(() => setLeft(remaining()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!left) return null;
  const v = VARIANTS[scope];
  const cta = <span className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${v.btn}`}>Buy a voucher <ArrowRight className="w-4 h-4" /></span>;

  return (
    <section className={`relative ${v.wrap} ${className}`} data-testid={`xmas-voucher-banner-${scope}`}>
      <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`shrink-0 w-11 h-11 rounded-full grid place-items-center bg-white/10 ${v.accent}`}><Gift className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="text-white font-bold text-base md:text-lg leading-tight">
              {v.title} <span className={v.accent}>· order by 24 Dec</span>
            </p>
            <p className="text-white/65 text-sm">{v.sub}. Emailed instantly, valid 6 months.</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid={`xmas-countdown-${scope}`}>
          <Unit value={left.d} label="days" box={v.box} testId={`xmas-countdown-days-${scope}`} />
          <Unit value={left.h} label="hrs" box={v.box} testId={`xmas-countdown-hours-${scope}`} />
          <Unit value={left.m} label="mins" box={v.box} testId={`xmas-countdown-mins-${scope}`} />
          <Unit value={left.s} label="secs" box={v.box} testId={`xmas-countdown-secs-${scope}`} />
        </div>
        {v.to.startsWith("#")
          ? <a href={v.to} data-testid={`xmas-banner-cta-${scope}`}>{cta}</a>
          : <Link to={v.to} data-testid={`xmas-banner-cta-${scope}`}>{cta}</Link>}
      </div>
    </section>
  );
};
